export default function findNodeRecursive(nodes, nodeId) {
  if (!nodes || !nodes?.length || !nodeId) return null;

  const currentNode = nodes?.find(node => node.id === nodeId);
  if (currentNode) {
    return currentNode;
  }

  return findNodeRecursive(
    nodes.flatMap(node => node.children || []),
    nodeId
  );
}

export const findDocumentRecursive = (directoryNodes, documentId) => {
  if (!directoryNodes || !directoryNodes?.length || !documentId) return null;
  const currentNode = directoryNodes?.find(node => node?.documents?.find(document => document.id === documentId));
  if (currentNode) {
    return currentNode;
  }

  return findDocumentRecursive(
    directoryNodes.flatMap(node => node.children || []),
    documentId
  );
};

/**
 * Encuentra un children y su nodo padre de forma recursiva.
 * Devuelve un objeto con el nodo que contiene el children y su nodo padre.
 *
 * @param {Array} directoryNodes - Array de nodos del directorio
 * @param {number} childrenId - ID del children a buscar
 * @returns {Object|null} { node, parentNode } o null si no se encuentra
 */
export const findChildrenWithParent = (directoryNodes, childrenId) => {
  if (!directoryNodes || !directoryNodes?.length || !childrenId) return null;

  const searchRecursive = nodes => {
    for (const node of nodes) {
      if (node?.children?.some(child => child.id === childrenId)) {
        return node;
      }
      if (node.children && node.children.length > 0) {
        const result = searchRecursive(node.children);
        if (result) return result;
      }
    }
    return null;
  };

  return searchRecursive(directoryNodes);
};

/**
 * Cierra un nodo padre y todas sus carpetas hijas de forma inmutable.
 * Devuelve un NUEVO array con los nodos actualizados.
 *
 * @param {Array} nodesArray - El array actual de nodos.
 * @param {number} parentId - El ID del nodo padre a cerrar.
 * @returns {Array} Un NUEVO array con las carpetas cerradas.
 */
export const closeParentAndChildren = (nodesArray, parentId) => {
  return nodesArray.map(node => {
    if (node.id === parentId) {
      return {
        ...node,
        isOpen: false,
        children: node.children ? closeParentAndChildren(node.children, node.id) : []
      };
    }

    if (node.parentId === parentId || node.dependFrom?.id === parentId) {
      return {
        ...node,
        isOpen: false,
        children: node.children ? closeParentAndChildren(node.children, node.id) : []
      };
    }

    if (node.children && node.children.length > 0) {
      const updatedChildren = closeParentAndChildren(node.children, parentId);
      if (updatedChildren !== node.children) {
        return {
          ...node,
          children: updatedChildren
        };
      }
    }

    return node;
  });
};

/**
 * Actualiza un nodo de forma inmutable dentro de un array de nodos anidados.
 * Devuelve un NUEVO array con el nodo actualizado.
 *
 * @param {Array} nodesArray - El array actual de nodos (ej: oldDirectoryNodes o un array de children).
 * @param {number} targetId - El ID del nodo a actualizar.
 * @param {Object} updatePayload - Los datos para actualizar/añadir al nodo encontrado.
 * Ej: { children: [], documents: [], hasDocumentsNextPage: true }
 * @returns {Array} Un NUEVO array con las actualizaciones aplicadas.
 */
export const updateNodeRecursively = (nodesArray, targetId, updatePayload) => {
  const targetNode = findNodeRecursive(nodesArray, targetId);
  if (!targetNode) return nodesArray;

  return nodesArray.map(node => {
    if (node.id === targetId) {
      return {
        ...node,
        parentId: node.dependFrom?.id,
        ...updatePayload
      };
    }
    if (!updatePayload.isOpen && node.children && node.children.length > 0) {
      const updatedChildren = updateNodeRecursively(node.children, targetId, updatePayload);
      return {
        ...node,
        children: updatedChildren
      };
    }
    const targetParentId = targetNode.dependFrom?.id;
    if (
      targetParentId &&
      (node.parentId === targetParentId || node.dependFrom?.id === targetParentId) &&
      node.id !== targetId
    ) {
      return {
        ...node,
        isOpen: false,
        children: node.children ? closeParentAndChildren(node.children, node.id) : []
      };
    }

    if (!targetParentId && !node.dependFrom?.id && node.id !== targetId) {
      return {
        ...node,
        isOpen: false,
        children: node.children ? closeParentAndChildren(node.children, node.id) : []
      };
    }

    if (node.children && node.children.length > 0) {
      const updatedChildren = updateNodeRecursively(node.children, targetId, updatePayload);

      if (updatedChildren !== node.children) {
        return {
          ...node,
          children: updatedChildren
        };
      }
    }

    return node;
  });
};

/**
 * Abre una carpeta y cierra sus hermanos de forma inmutable.
 * Devuelve un NUEVO array con los nodos actualizados.
 *
 * @param {Array} nodesArray - El array actual de nodos.
 * @param {number} folderId - El ID de la carpeta a abrir.
 * @returns {Array} Un NUEVO array con la carpeta abierta y hermanos cerrados.
 */
export const openFolderAndCloseSiblings = (nodesArray, folderId) => {
  const targetNode = findNodeRecursive(nodesArray, folderId);
  if (!targetNode) return nodesArray;

  return nodesArray.map(node => {
    if (node.id === folderId) {
      return {
        ...node,
        isOpen: true
      };
    }

    if (
      targetNode.parentId &&
      (node.parentId === targetNode.parentId || node.dependFrom?.id === targetNode.parentId) &&
      node.id !== folderId
    ) {
      return {
        ...node,
        isOpen: false,
        children: node.children ? closeParentAndChildren(node.children, node.id) : []
      };
    }

    if (node.children && node.children.length > 0) {
      const updatedChildren = openFolderAndCloseSiblings(node.children, folderId);
      if (updatedChildren !== node.children) {
        return {
          ...node,
          children: updatedChildren
        };
      }
    }
    return { ...node, isOpen: false };
  });
};
